import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import DomPurify from 'dompurify';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { ordersApi } from '../../../../services/apiServices/user/orders';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import {
  AccountTypes,
  ButtonClassNames,
  InputTextFormat,
  ModalVariants,
  LocalStorageItems,
  InputRegexPattern,
} from '../../../../models/Common';
import { ApiError } from '../../../../models/ApiError';
import { FormInputText } from '../../../inputs/FormInputText';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { FormInputCheckbox } from '../../../inputs/FormInputCheckbox';
import { ReactComponent as CheckBoxIcon } from '../../../../assets/images/EmptyCheckBox.svg';

interface RestoreCallbackProps {
  service?: string;
  accountType?: string;
}

const defaultProps = {
  service: '',
  accountType: '',
};

export const RestoreCallback: FC<RestoreCallbackProps> = ({ service = '', accountType }) => {
  const { t } = useTranslation();
  const sanitizer = DomPurify.sanitize;
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [authorizedCallback, authorizedCallbackState] = ordersApi.useAuthorizedCallbackMutation();
  const { isLoading } = authorizedCallbackState;
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoData, isSuccess: isUserDataDownloaded } = controlledAccount
    ? useGetUserInfoMultiAccQuery(
        { subAccountId: JSON.parse(controlledAccount).UserID },
        { refetchOnMountOrArgChange: true },
      )
    : useGetUserInfoQuery('', { refetchOnMountOrArgChange: true });
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const cachedData = localStorage.getItem(`${LocalStorageItems.CallMeBack}-${userInfoState.id}`);
  const showDefaults = cachedData && JSON.parse(cachedData)[userInfoState.id];
  const defaultPhone =
    showDefaults && JSON.parse(cachedData)[userInfoState.id].phone
      ? JSON.parse(cachedData)[userInfoState.id].phone
      : '';
  const formattedServiceName =
    accountType === AccountTypes.Iptv ||
    accountType === AccountTypes.Union ||
    (accountType === AccountTypes.Internet && !parseInt(service, 10)) ||
    (accountType === AccountTypes.Ctv && service === t('services.ctv'))
      ? service
      : `${t(`common.${accountType}`)} ${service}`;
  const methods = useForm({
    defaultValues: {
      phone: userInfoState.SmsSettings?.Phone || defaultPhone,
      userComment: '',
      viber: false,
    },
  });
  const { handleSubmit, setError } = methods;

  const handleSaveFormData = (phone: string) => {
    const data: Partial<{ phone: string }> = {};

    if (phone !== userInfoState.SmsSettings?.Phone) {
      data.phone = phone;
    }
    if (data.phone) {
      localStorage.setItem(
        `${LocalStorageItems.CallMeBack}-${userInfoState.id}`,
        JSON.stringify({ [userInfoState.id]: data }),
      );
    } else {
      localStorage.removeItem(`${LocalStorageItems.CallMeBack}-${userInfoState.id}`);
    }
  };

  const handleSendRequest = async ({
    phone,
    userComment,
    viber,
  }: {
    phone: string;
    userComment: string;
    viber: boolean;
  }) => {
    const restore = `Заявка на восстановление: ${formattedServiceName}.`;
    const site = `Сайт: ${process.env.REACT_APP_NAME || ''}.`;
    const agreement = isUserDataDownloaded && `№ договора: ${userInfoData.id}.`;
    const street = isUserDataDownloaded && userInfoData?.Addresses[0].Street.NameRu;
    const house = isUserDataDownloaded && userInfoData?.Addresses[0].House;
    const flat = isUserDataDownloaded && userInfoData?.Addresses[0].Flat;
    const address = `Домашний адрес: ул. ${street}, дом ${house}${flat ? `, кв. ${flat}` : ''}.`;
    const userPhone = `Телефон: +38${phone}`;
    const commentMessage = `${site} ${restore} ${agreement} ${userPhone} ${address}${
      viber ? ` Дополнительная связь: ${viber ? 'Viber.' : ''}` : ''
    }${userComment.trim() ? ` Комментарий: ${userComment.trim()}` : ''}`;

    try {
      await authorizedCallback({ phone, comment: commentMessage })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.Success,
              content: {
                title: `${t('modals.thankYou')}`,
                description: `${t('modals.requestAcceptedAnOperatorWillContactYouShortly')}`,
              },
            }),
          );
          handleSaveFormData(phone);
        });
    } catch (err: unknown) {
      const error = err as ApiError;
      setError('phone', {
        type: 'server',
        message: error?.data?.error?.message,
      });
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.serviceRestoration')}</article>
        <p
          className="modals__header__description"
          dangerouslySetInnerHTML={{
            __html: sanitizer(t('modals.serviceWasDisabled', { service: formattedServiceName })),
          }}
        />
        <p className="modals__header__description">{t('modals.exactAmountToBePaid')}</p>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleSendRequest)}>
          <FormInputText
            name="phone"
            className="primary"
            label={t('input.phone')}
            isRequired
            minLength={10}
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
          />
          <FormInputText name="userComment" className="primary" label={t('input.comment')} maxLength={100} />
          <div style={{ textAlign: 'left' }}>
            <p className="additional__connection__title">{t('callMeBack.additionalConnection')}</p>
            <FormInputCheckbox
              name="viber"
              icon={<CheckBoxIcon />}
              label={<div className="modals__form__label viber">{t('callMeBack.connectionViber')} </div>}
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('common.sendRequest')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

RestoreCallback.defaultProps = defaultProps;
